/**
 * Getting Started Checklist — Iter 20 (audit Finding 4 / P0).
 *
 * Replaces (or co-exists with) the welcome video as the primary onboarding
 * surface on /app. Five concrete steps; each step routes the user to the
 * actual module that does the work. Completion is per-user, persisted to
 * /api/users/me/getting-started.
 *
 * Co-existence rules (per the user's product brief):
 *   • If platform_settings.welcome_video_url is empty → this checklist is
 *     the primary surface (it is rendered at the top of /app).
 *   • If a welcome video URL is set later, that video card renders ABOVE
 *     the checklist; the checklist continues to live on /app until the
 *     user dismisses it or completes 5/5.
 *   • Dismissal is reversible from Settings → "Show getting started again".
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ListChecks, Check, ArrowRight, X, GraduationCap, Users, FileText,
  BookOpen, Plug,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { AddFirstLeadModal } from "@/components/AddFirstLeadModal";

const STEP_ICONS = {
  talk_to_sam:           GraduationCap,
  add_first_lead:        Users,
  generate_content:      FileText,
  upload_kb_doc:         BookOpen,
  connect_or_chat_agent: Plug,
};

const STEP_ACCENT = {
  talk_to_sam:           "text-indigo-300 bg-indigo-500/15",
  add_first_lead:        "text-violet-300 bg-violet-500/15",
  generate_content:      "text-pink-300 bg-pink-500/15",
  upload_kb_doc:         "text-emerald-300 bg-emerald-500/15",
  connect_or_chat_agent: "text-fuchsia-300 bg-fuchsia-500/15",
};

export function GettingStartedChecklist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/users/me/getting-started");
      setData(r.data);
    } catch (err) {
      // Silently no-op — checklist is non-critical.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const markComplete = useCallback(async (stepId) => {
    try {
      const r = await api.post(`/users/me/getting-started/${stepId}/complete`);
      setData(r.data);
    } catch (err) {
      toast.error(errorMessage(err) || "Could not save");
    }
  }, []);

  const dismiss = useCallback(async () => {
    setDismissing(true);
    try {
      const r = await api.post("/users/me/getting-started/dismiss");
      setData(r.data);
      toast("Checklist hidden — restore it from Settings any time.");
    } catch (err) {
      toast.error(errorMessage(err) || "Could not dismiss");
    } finally {
      setDismissing(false);
    }
  }, []);

  if (loading || !data) return null;
  if (data.dismissed_at) return null;
  if (data.completed_count >= data.total) {
    // Quiet celebration — render once for ~1 cycle, then collapse to nothing.
    return null;
  }

  const stepRender = (step) => {
    const Icon = STEP_ICONS[step.id] || ListChecks;
    const accent = STEP_ACCENT[step.id] || "text-fuchsia-300 bg-fuchsia-500/15";
    const completed = step.completed;

    // "Add first lead" is the only step that opens a modal instead of a
    // route — the audit said the CSV-first flow is a Tier 2 fix, so this
    // minimal paste/form modal is the unblock for today.
    const cta = step.id === "add_first_lead" ? (
      <Button
        variant="ghost"
        onClick={() => setLeadModalOpen(true)}
        disabled={completed}
        className={completed
          ? "h-7 px-2 text-xs text-emerald-300"
          : "h-7 px-2 text-xs text-white/65 hover:text-white"
        }
        data-testid={`gs-step-cta-${step.id}`}
      >
        {completed ? "Done" : step.cta_label} {!completed && <ArrowRight className="ml-1 h-3 w-3" />}
      </Button>
    ) : (
      <Button
        asChild
        variant="ghost"
        disabled={completed}
        className={completed
          ? "h-7 px-2 text-xs text-emerald-300"
          : "h-7 px-2 text-xs text-white/65 hover:text-white"
        }
        data-testid={`gs-step-cta-${step.id}`}
      >
        {completed ? (
          <span>Done</span>
        ) : (
          <Link to={step.cta_path} onClick={() => markComplete(step.id)}>
            {step.cta_label} <ArrowRight className="ml-1 h-3 w-3" />
          </Link>
        )}
      </Button>
    );

    return (
      <li
        key={step.id}
        className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition ${
          completed
            ? "border-emerald-500/20 bg-emerald-500/[0.04]"
            : "border-white/5 bg-white/[0.02] hover:bg-white/[0.05]"
        }`}
        data-testid={`gs-step-${step.id}`}
      >
        <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${accent}`}>
          {completed
            ? <Check className="h-3.5 w-3.5 text-emerald-300" data-testid={`gs-step-check-${step.id}`} />
            : <Icon className="h-3.5 w-3.5" />
          }
        </div>
        <div className="min-w-0 flex-1">
          <div className={`truncate text-sm ${completed ? "text-white/55 line-through" : "text-white"}`}>
            {step.label}
          </div>
          <div className="truncate text-xs text-white/45">{step.description}</div>
        </div>
        {cta}
      </li>
    );
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="glass mt-6 overflow-hidden rounded-2xl"
        data-testid="getting-started-checklist"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 px-5 py-3">
          <div className="flex items-center gap-2 text-eyebrow text-indigo-300">
            <ListChecks className="h-3.5 w-3.5" /> Getting Started
            <span className="ml-2 rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] tracking-normal text-white/65" data-testid="gs-progress">
              {data.completed_count}/{data.total}
            </span>
          </div>
          <button
            onClick={dismiss}
            disabled={dismissing}
            className="text-xs text-white/45 transition hover:text-white/85"
            data-testid="gs-dismiss"
          >
            <X className="mr-1 inline h-3 w-3" /> Hide
          </button>
        </div>
        <ul className="space-y-2 px-5 py-4">
          {data.steps.map(stepRender)}
        </ul>
      </motion.div>

      <AddFirstLeadModal
        open={leadModalOpen}
        onOpenChange={setLeadModalOpen}
        onCreated={async () => {
          setLeadModalOpen(false);
          await markComplete("add_first_lead");
        }}
      />
    </>
  );
}
